/**
 * Dream Hierarchy Display Component - V2
 * 
 * Displays the nested dream hierarchy from yanamas V3 API:
 * Dreams → Aspirations → Goals → Tasks → Action Items
 * 
 * Features:
 * - In-place editing of items (type-specific fields)
 * - Skip items with optional feedback
 * - Draft/edit state tracking
 * - Save all items to Life Map when ready
 * - NO Accept/Accept All buttons - items are saved via main button
 */

import React, { useState, useCallback, useMemo } from 'react'
import {
 ChevronDown, ChevronRight, Star, Target, CheckSquare, ListTodo,
 CheckCircle2, AlertTriangle, BookOpen, Lightbulb, Clock, X,
 Loader2, Edit2, Save, XCircle, MessageSquare, Calendar, Repeat,
 Zap, AlertCircle, Check, User, Users
} from 'lucide-react'
import { HortonElephantGame } from './HortonElephantGame'
import type { ComposedResponse } from '../../services/hortonV3Service'
import { parseRRule, buildRRule, defaultSpec, rruleFromLegacy } from '../../utils/rrule'
import { BRAND } from '../../config/brand'

// Icon mapping for hierarchy types
const typeIcons: Record<string, React.ReactNode> = {
 dream: <Star className="w-5 h-5 text-warn" />,
 aspiration: <Target className="w-5 h-5 text-info" />,
 goal: <CheckSquare className="w-5 h-5 text-brand" />,
 task: <ListTodo className="w-5 h-5 text-ok" />,
 action_item: <CheckCircle2 className="w-5 h-5 text-ok" />,
 roadblock: <AlertTriangle className="w-5 h-5 text-warn" />,
 knowledge_skill: <BookOpen className="w-5 h-5 text-info" />,
 skill: <Lightbulb className="w-5 h-5 text-info" />,
 habit: <Repeat className="w-5 h-5 text-ok" />,
 event: <Clock className="w-5 h-5 text-bad" />,
}

// Color classes for hierarchy types
const typeColors: Record<string, string> = {
 dream: 'border-l-yellow-400 bg-tint-warn-soft',
 aspiration: 'border-l-tint-info-rule bg-tint-info-soft',
 goal: 'border-l-blue-400 bg-brand-subtle',
 task: 'border-l-green-400 bg-tint-ok-soft',
 action_item: 'border-l-tint-ok-rule bg-tint-ok-veil',
 roadblock: 'border-l-orange-400 bg-tint-warn-soft',
 knowledge_skill: 'border-l-tint-info-rule bg-tint-info-soft',
 skill: 'border-l-tint-info-rule bg-tint-info-veil',
 habit: 'border-l-tint-ok-rule bg-tint-ok-veil',
 event: 'border-l-tint-bad-rule bg-tint-bad-veil',
}

// Life area badges
const lifeAreaColors: Record<string, string> = {
 health: 'bg-tint-ok-fill text-ok ',
 career: 'bg-brand-subtle text-brand ',
 relationships: 'bg-tint-bad-fill text-bad ',
 money: 'bg-tint-warn-fill text-warn ',
 learning: 'bg-tint-info-fill text-info ',
 personal_growth: 'bg-tint-info-fill text-info ',
 productivity: 'bg-tint-warn-fill text-warn ',
 other: 'bg-surface-alt text-secondary /30 ',
}

// Item status tracking
export type ItemStatus = 'pending' | 'editing' | 'edited' | 'rejected'

// Rich recurrence object from planner
export interface RecurrenceRule {
 type: 'daily' | 'weekly' | 'monthly' | 'custom' | 'none'
 interval?: number
 days_of_week?: string[]
 end_condition?: 'until_done' | 'until_date' | 'count'
 end_date?: string
 count?: number
}

// Editable item fields by type.
// SINGLE editable-field contract: the edit form (ItemEditForm) may only write
// keys declared here, and the save path (HortonChat filterRejectedItems)
// copies EVERY key except title/description/life_area/recurrence(_rule) into
// the outgoing `fields` bag — so adding a field here is all that's needed for
// it to survive Save. Never re-list fields in a save-side whitelist.
export interface ItemEdit {
 title: string
 description?: string
 // Common fields
 life_area?: string
 priority?: 'low' | 'medium' | 'high' | 'critical'
 // Timeline fields (for dreams, aspirations, goals)
 target_date?: string
 start_date?: string
 // Task-specific fields
 duration_minutes?: number
 recurrence?: 'none' | 'daily' | 'weekly' | 'biweekly' | 'monthly'
 energy_level?: 'low' | 'medium' | 'high'
 // Rich scheduling intent from planner
 recurrence_rule?: RecurrenceRule
 preferred_days?: string[]
 preferred_time_window?: string
 preferred_start_time?: string // "HH:MM" 24h
 schedule_candidate?: boolean
 // Action item specific
 scheduled_time?: string
 // Roadblock specific
 severity?: 'low' | 'medium' | 'high'
 // Habit-specific fields
 habit_status?: string
 target_cadence?: string
 rrule?: string
 follow_through_mode?: string
 nag_style?: string
 // Skill-specific fields
 skill_status?: string
 current_level?: string
 target_level?: string
}

// Rejected item with optional comment
export interface RejectedItem {
 type: string
 title: string
 originalDescription?: string
 rejectionComment?: string
 rejectedAt: string
}

// State for an item in the hierarchy
export interface ItemState {
 status: ItemStatus
 originalItem: any
 editedFields?: ItemEdit
 rejectionComment?: string
}

// Props for the edit form
interface ItemEditFormProps {
 item: any
 itemType: string
 editedFields: ItemEdit
 onChange: (fields: ItemEdit) => void
 onSave: () => void
 onCancel: () => void
}

function ItemEditForm({ itemType, editedFields, onChange, onSave, onCancel }: ItemEditFormProps) {
 // Render type-specific fields
 const renderTypeSpecificFields = () => {
 switch (itemType) {
 case 'dream':
 case 'aspiration':
 return (
 <>
 <div className="grid grid-cols-2 gap-2">
 <div>
 <label className="text-xs text-muted">Target Date</label>
 <input
 type="date"
 value={editedFields.target_date || ''}
 onChange={(e) => onChange({ ...editedFields, target_date: e.target.value })}
 className="w-full px-2 py-1 text-sm rounded border border-strong bg-surface"
 />
 </div>
 <div>
 <label className="text-xs text-muted">Life Area</label>
 <select
 value={editedFields.life_area || ''}
 onChange={(e) => onChange({ ...editedFields, life_area: e.target.value })}
 className="w-full px-2 py-1 text-sm rounded border border-strong bg-surface"
 >
 <option value="">Select...</option>
 <option value="health">Health</option>
 <option value="career">Career</option>
 <option value="relationships">Relationships</option>
 <option value="money">Money</option>
 <option value="learning">Learning</option>
 <option value="personal_growth">Personal Growth</option>
 <option value="productivity">Productivity</option>
 <option value="other">Other</option>
 </select>
 </div>
 </div>
 </>
 )

 case 'goal':
 return (
 <>
 <div className="grid grid-cols-2 gap-2">
 <div>
 <label className="text-xs text-muted">Start Date</label>
 <input
 type="date"
 value={editedFields.start_date || ''}
 onChange={(e) => onChange({ ...editedFields, start_date: e.target.value })}
 className="w-full px-2 py-1 text-sm rounded border border-strong bg-surface"
 />
 </div>
 <div>
 <label className="text-xs text-muted">Target Date</label>
 <input
 type="date"
 value={editedFields.target_date || ''}
 onChange={(e) => onChange({ ...editedFields, target_date: e.target.value })}
 className="w-full px-2 py-1 text-sm rounded border border-strong bg-surface"
 />
 </div>
 </div>
 <div className="grid grid-cols-2 gap-2">
 <div>
 <label className="text-xs text-muted">Priority</label>
 <select
 value={editedFields.priority || 'medium'}
 onChange={(e) => onChange({ ...editedFields, priority: e.target.value as any })}
 className="w-full px-2 py-1 text-sm rounded border border-strong bg-surface"
 >
 <option value="low">Low</option>
 <option value="medium">Medium</option>
 <option value="high">High</option>
 <option value="critical">Critical</option>
 </select>
 </div>
 <div>
 <label className="text-xs text-muted">Life Area</label>
 <select
 value={editedFields.life_area || ''}
 onChange={(e) => onChange({ ...editedFields, life_area: e.target.value })}
 className="w-full px-2 py-1 text-sm rounded border border-strong bg-surface"
 >
 <option value="">Select...</option>
 <option value="health">Health</option>
 <option value="career">Career</option>
 <option value="relationships">Relationships</option>
 <option value="money">Money</option>
 <option value="learning">Learning</option>
 <option value="personal_growth">Personal Growth</option>
 <option value="productivity">Productivity</option>
 <option value="other">Other</option>
 </select>
 </div>
 </div>
 </>
 )

 case 'task':
 // Helper to format recurrence for display
 const formatRecurrenceDisplay = () => {
 const rule = editedFields.recurrence_rule
 if (!rule || rule.type === 'none') return null
 
 const dayMap: Record<string, string> = { 'mon': 'Mon', 'tue': 'Tue', 'wed': 'Wed', 'thu': 'Thu', 'fri': 'Fri', 'sat': 'Sat', 'sun': 'Sun' }
 let pattern = ''
 
 if (rule.type === 'daily') {
 pattern = rule.interval === 1 ? 'Daily' : `Every ${rule.interval} days`
 } else if (rule.type === 'weekly') {
 const daysStr = rule.days_of_week?.map(d => dayMap[d.toLowerCase()] || d).join(', ') || ''
 pattern = rule.interval === 1 
 ? `Weekly${daysStr ? ` on ${daysStr}` : ''}`
 : `Every ${rule.interval} weeks${daysStr ? ` on ${daysStr}` : ''}`
 } else if (rule.type === 'monthly') {
 pattern = rule.interval === 1 ? 'Monthly' : `Every ${rule.interval} months`
 }
 
 if (rule.end_condition === 'until_done') {
 pattern += ' (ongoing)'
 }
 
 return pattern
 }

 const hasRichRecurrence = editedFields.recurrence_rule && editedFields.recurrence_rule.type !== 'none'
 const hasPreferredDays = editedFields.preferred_days && editedFields.preferred_days.length > 0
 const hasTimeWindow = !!editedFields.preferred_time_window
 const hasStartTime = !!editedFields.preferred_start_time
 const hasDuration = !!editedFields.duration_minutes
 const hasSchedulingIntent = hasRichRecurrence || hasPreferredDays || hasTimeWindow || hasStartTime || hasDuration

 return (
 <>
 {/* Scheduling Intent from Planner (read-only display) */}
 {hasSchedulingIntent && (
 <div className="p-3 bg-brand-subtle rounded-lg border border-brand-subtle mb-3">
 <div className="flex items-center gap-2 text-sm font-medium text-brand mb-2">
 <Repeat className="w-4 h-4" />
 🎯 Planner Scheduling Intent
 </div>
 <div className="space-y-1.5 text-sm">
 {hasRichRecurrence && (
 <div className="flex items-center gap-2 text-brand">
 <span className="font-medium">🔄 Recurrence:</span>
 <span className="bg-brand-subtle px-2 py-0.5 rounded">
 {formatRecurrenceDisplay()}
 </span>
 </div>
 )}
 {hasPreferredDays && !hasRichRecurrence && (
 <div className="flex items-center gap-2 text-brand">
 <span className="font-medium">📅 Preferred Days:</span>
 <span className="bg-brand-subtle px-2 py-0.5 rounded">
 {editedFields.preferred_days?.map(d => d.charAt(0).toUpperCase() + d.slice(1)).join(', ')}
 </span>
 </div>
 )}
 {hasTimeWindow && (
 <div className="flex items-center gap-2 text-brand">
 <span className="font-medium">⏰ Time Window:</span>
 <span className="bg-brand-subtle px-2 py-0.5 rounded">
 {editedFields.preferred_time_window === 'early_morning' ? '🌅 Early Morning' :
 editedFields.preferred_time_window === 'morning' ? '☀️ Morning' :
 editedFields.preferred_time_window === 'afternoon' ? '🌤️ Afternoon' :
 editedFields.preferred_time_window === 'evening' ? '🌆 Evening' :
 editedFields.preferred_time_window === 'night' ? '🌙 Night' :
 editedFields.preferred_time_window}
 </span>
 </div>
 )}
 {hasStartTime && (
 <div className="flex items-center gap-2 text-brand">
 <span className="font-medium">⏱ Suggested time:</span>
 <span className="bg-brand-subtle px-2 py-0.5 rounded">
 {editedFields.preferred_start_time}
 </span>
 </div>
 )}
 {hasDuration && (
 <div className="flex items-center gap-2 text-brand">
 <span className="font-medium">⏲ Duration:</span>
 <span className="bg-brand-subtle px-2 py-0.5 rounded">
 {editedFields.duration_minutes} min
 </span>
 </div>
 )}
 {editedFields.schedule_candidate && (
 <div className="text-ok font-medium flex items-center gap-1">
 <Check className="w-4 h-4" />
 Ready for scheduling
 </div>
 )}
 </div>
 </div>
 )}

 <div className="grid grid-cols-3 gap-2">
 <div>
 <label className="text-xs text-muted flex items-center gap-1">
 <Clock className="w-3 h-3" /> Duration (min)
 </label>
 <input
 type="number"
 min="5"
 max="480"
 step="5"
 value={editedFields.duration_minutes || 30}
 onChange={(e) => onChange({ ...editedFields, duration_minutes: parseInt(e.target.value) || 30 })}
 className="w-full px-2 py-1 text-sm rounded border border-strong bg-surface"
 />
 </div>
 <div>
 <label className="text-xs text-muted flex items-center gap-1">
 <Repeat className="w-3 h-3" /> Recurrence
 </label>
 <select
 value={hasRichRecurrence ? editedFields.recurrence_rule?.type : (editedFields.recurrence || 'none')}
 onChange={(e) => {
 const newType = e.target.value as any
 if (newType === 'none') {
 onChange({ ...editedFields, recurrence: 'none', recurrence_rule: undefined })
 } else {
 onChange({ 
 ...editedFields, 
 recurrence: newType,
 recurrence_rule: {
 ...editedFields.recurrence_rule,
 type: newType,
 interval: editedFields.recurrence_rule?.interval || 1
 }
 })
 }
 }}
 className="w-full px-2 py-1 text-sm rounded border border-strong bg-surface"
 >
 <option value="none">None</option>
 <option value="daily">Daily</option>
 <option value="weekly">Weekly</option>
 <option value="biweekly">Bi-weekly</option>
 <option value="monthly">Monthly</option>
 </select>
 </div>
 <div>
 <label className="text-xs text-muted flex items-center gap-1">
 <Zap className="w-3 h-3" /> Energy
 </label>
 <select
 value={editedFields.energy_level || 'medium'}
 onChange={(e) => onChange({ ...editedFields, energy_level: e.target.value as any })}
 className="w-full px-2 py-1 text-sm rounded border border-strong bg-surface"
 >
 <option value="low">Low ⚡</option>
 <option value="medium">Medium ⚡⚡</option>
 <option value="high">High ⚡⚡⚡</option>
 </select>
 </div>
 </div>

 {/* Preferred Time Window + Suggested start time */}
 <div className="grid grid-cols-3 gap-2">
 <div>
 <label className="text-xs text-muted">Priority</label>
 <select
 value={editedFields.priority || 'medium'}
 onChange={(e) => onChange({ ...editedFields, priority: e.target.value as any })}
 className="w-full px-2 py-1 text-sm rounded border border-strong bg-surface"
 >
 <option value="low">Low</option>
 <option value="medium">Medium</option>
 <option value="high">High</option>
 <option value="critical">Critical</option>
 </select>
 </div>
 <div>
 <label className="text-xs text-muted flex items-center gap-1">
 <Calendar className="w-3 h-3" /> Preferred Time
 </label>
 <select
 value={editedFields.preferred_time_window || ''}
 onChange={(e) => onChange({ ...editedFields, preferred_time_window: e.target.value || undefined })}
 className="w-full px-2 py-1 text-sm rounded border border-strong bg-surface"
 >
 <option value="">Any time</option>
 <option value="early_morning">🌅 Early Morning</option>
 <option value="morning">☀️ Morning</option>
 <option value="afternoon">🌤️ Afternoon</option>
 <option value="evening">🌆 Evening</option>
 <option value="night">🌙 Night</option>
 </select>
 </div>
 <div>
 <label className="text-xs text-muted flex items-center gap-1">
 <Clock className="w-3 h-3" /> Suggested Time
 </label>
 <input
 type="time"
 value={editedFields.preferred_start_time || ''}
 onChange={(e) => onChange({ ...editedFields, preferred_start_time: e.target.value || undefined })}
 className="w-full px-2 py-1 text-sm rounded border border-strong bg-surface"
 />
 </div>
 </div>

 {/* Preferred Days multi-select (checkboxes) */}
 <div>
 <label className="text-xs text-muted mb-1 block">Preferred Days</label>
 <div className="flex flex-wrap gap-1">
 {['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'].map(day => {
 const isSelected = editedFields.preferred_days?.includes(day) || 
 editedFields.recurrence_rule?.days_of_week?.includes(day)
 return (
 <button
 key={day}
 type="button"
 onClick={() => {
 const currentDays = editedFields.preferred_days || editedFields.recurrence_rule?.days_of_week || []
 const newDays = isSelected 
 ? currentDays.filter(d => d !== day)
 : [...currentDays, day]
 onChange({ 
 ...editedFields, 
 preferred_days: newDays,
 recurrence_rule: editedFields.recurrence_rule ? {
 ...editedFields.recurrence_rule,
 days_of_week: newDays
 } : undefined
 })
 }}
 className={`px-2 py-1 text-xs rounded border transition-colors ${
 isSelected 
 ? 'bg-brand text-on-brand border-brand' 
 : 'bg-surface border-strong hover:border-brand'
 }`}
 >
 {day.charAt(0).toUpperCase() + day.slice(1)}
 </button>
 )
 })}
 </div>
 </div>
 </>
 )

 case 'action_item':
 return (
 <>
 <div className="grid grid-cols-2 gap-2">
 <div>
 <label className="text-xs text-muted flex items-center gap-1">
 <Clock className="w-3 h-3" /> Duration (min)
 </label>
 <input
 type="number"
 min="5"
 max="480"
 step="5"
 value={editedFields.duration_minutes || 15}
 onChange={(e) => onChange({ ...editedFields, duration_minutes: parseInt(e.target.value) || 15 })}
 className="w-full px-2 py-1 text-sm rounded border border-strong bg-surface"
 />
 </div>
 <div>
 <label className="text-xs text-muted flex items-center gap-1">
 <Calendar className="w-3 h-3" /> Scheduled Time
 </label>
 <input
 type="datetime-local"
 value={editedFields.scheduled_time || ''}
 onChange={(e) => onChange({ ...editedFields, scheduled_time: e.target.value })}
 className="w-full px-2 py-1 text-sm rounded border border-strong bg-surface"
 />
 </div>
 </div>
 </>
 )

 case 'roadblock':
 return (
 <div>
 <label className="text-xs text-muted">Severity</label>
 <select
 value={editedFields.severity || 'medium'}
 onChange={(e) => onChange({ ...editedFields, severity: e.target.value as any })}
 className="w-full px-2 py-1 text-sm rounded border border-strong bg-surface"
 >
 <option value="low">Low</option>
 <option value="medium">Medium</option>
 <option value="high">High</option>
 </select>
 </div>
 )

 case 'skill':
 return (
 <div className="grid grid-cols-2 gap-2">
 <div>
 <label className="text-xs text-muted">Skill Status</label>
 <select
 value={editedFields.skill_status || 'active'}
 onChange={(e) => onChange({ ...editedFields, skill_status: e.target.value })}
 className="w-full px-2 py-1 text-sm rounded border border-strong bg-surface"
 >
 <option value="active">Active</option>
 <option value="developing">Developing</option>
 <option value="ready_for_review">Ready for Review</option>
 <option value="sufficient_for_goal">Sufficient</option>
 </select>
 </div>
 <div>
 <label className="text-xs text-muted">Current Level</label>
 <select
 value={editedFields.current_level || 'novice'}
 onChange={(e) => onChange({ ...editedFields, current_level: e.target.value })}
 className="w-full px-2 py-1 text-sm rounded border border-strong bg-surface"
 >
 <option value="novice">Novice</option>
 <option value="beginner">Beginner</option>
 <option value="intermediate">Intermediate</option>
 <option value="advanced">Advanced</option>
 <option value="expert">Expert</option>
 </select>
 </div>
 <div>
 <label className="text-xs text-muted">Target Level</label>
 <select
 value={editedFields.target_level || 'intermediate'}
 onChange={(e) => onChange({ ...editedFields, target_level: e.target.value })}
 className="w-full px-2 py-1 text-sm rounded border border-strong bg-surface"
 >
 <option value="novice">Novice</option>
 <option value="beginner">Beginner</option>
 <option value="intermediate">Intermediate</option>
 <option value="advanced">Advanced</option>
 <option value="expert">Expert</option>
 </select>
 </div>
 </div>
 )

 case 'habit':
 return (
 <div className="space-y-2">
 <div className="grid grid-cols-2 gap-2">
 <div>
 <label className="text-xs text-muted">Habit Status</label>
 <select
 value={editedFields.habit_status || 'forming'}
 onChange={(e) => onChange({ ...editedFields, habit_status: e.target.value })}
 className="w-full px-2 py-1 text-sm rounded border border-strong bg-surface"
 >
 <option value="active">Active</option>
 <option value="forming">Forming</option>
 <option value="stable">Stable</option>
 <option value="internalized">Internalized</option>
 </select>
 </div>
 <div>
 <label className="text-xs text-muted">Target Cadence</label>
 <select
 value={editedFields.target_cadence || 'daily'}
 onChange={(e) => {
 const v = e.target.value
 const base = editedFields.rrule ? parseRRule(editedFields.rrule) : defaultSpec()
 const { spec: seeded } = rruleFromLegacy(
 v,
 editedFields.preferred_days,
 editedFields.preferred_time_window,
 )
 onChange({
 ...editedFields,
 target_cadence: v,
 rrule: buildRRule({
 ...base,
 freq: seeded.freq,
 interval: seeded.interval,
 byweekday: seeded.byweekday,
 monthlyMode: seeded.monthlyMode,
 bymonthday: seeded.bymonthday,
 bysetpos: seeded.bysetpos,
 bydayForMonthly: seeded.bydayForMonthly,
 }),
 })
 }}
 className="w-full px-2 py-1 text-sm rounded border border-strong bg-surface"
 >
 <option value="daily">Daily</option>
 <option value="weekly">Weekly</option>
 <option value="biweekly">Biweekly</option>
 <option value="monthly">Monthly</option>
 </select>
 </div>
 </div>

 {/* Scheduling — duration, suggested start time, preferred window */}
 <div className="grid grid-cols-3 gap-2">
 <div>
 <label className="text-xs text-muted flex items-center gap-1">
 <Clock className="w-3 h-3" /> Duration (min)
 </label>
 <input
 type="number"
 min="1"
 max="480"
 step="5"
 placeholder="—"
 value={editedFields.duration_minutes ?? ''}
 onChange={(e) => {
 const raw = e.target.value
 onChange({
 ...editedFields,
 duration_minutes: raw === '' ? undefined : parseInt(raw, 10) || undefined,
 })
 }}
 className="w-full px-2 py-1 text-sm rounded border border-strong bg-surface"
 />
 </div>
 <div>
 <label className="text-xs text-muted flex items-center gap-1">
 <Clock className="w-3 h-3" /> Suggested Time
 </label>
 <input
 type="time"
 value={editedFields.preferred_start_time || ''}
 onChange={(e) => onChange({ ...editedFields, preferred_start_time: e.target.value || undefined })}
 className="w-full px-2 py-1 text-sm rounded border border-strong bg-surface"
 />
 </div>
 <div>
 <label className="text-xs text-muted flex items-center gap-1">
 <Calendar className="w-3 h-3" /> Preferred Time
 </label>
 <select
 value={editedFields.preferred_time_window || ''}
 onChange={(e) => onChange({ ...editedFields, preferred_time_window: e.target.value || undefined })}
 className="w-full px-2 py-1 text-sm rounded border border-strong bg-surface"
 >
 <option value="">Any time</option>
 <option value="early_morning">🌅 Early Morning</option>
 <option value="morning">☀️ Morning</option>
 <option value="afternoon">🌤️ Afternoon</option>
 <option value="evening">🌆 Evening</option>
 <option value="night">🌙 Night</option>
 </select>
 </div>
 </div>

 {/* Preferred Days multi-select */}
 <div>
 <label className="text-xs text-muted mb-1 block">Preferred Days</label>
 <div className="flex flex-wrap gap-1">
 {['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'].map(day => {
 const currentDays = editedFields.preferred_days || []
 const isSelected = currentDays.includes(day)
 return (
 <button
 key={day}
 type="button"
 onClick={() => {
 const newDays = isSelected
 ? currentDays.filter(d => d !== day)
 : [...currentDays, day]
 onChange({ ...editedFields, preferred_days: newDays })
 }}
 className={`px-2 py-1 text-xs rounded border transition-colors ${
 isSelected
 ? 'bg-brand text-on-brand border-brand'
 : 'bg-surface border-strong hover:border-brand'
 }`}
 >
 {day.charAt(0).toUpperCase() + day.slice(1)}
 </button>
 )
 })}
 </div>
 </div>

 <div className="grid grid-cols-2 gap-2">
 <div>
 <label className="text-xs text-muted">Follow-through</label>
 <select
 value={editedFields.follow_through_mode || 'hard_window'}
 onChange={(e) => onChange({ ...editedFields, follow_through_mode: e.target.value })}
 className="w-full px-2 py-1 text-sm rounded border border-strong bg-surface"
 >
 <option value="hard_window">Hard window</option>
 <option value="soft_window">Soft window</option>
 <option value="all_day">All day</option>
 </select>
 </div>
 <div>
 <label className="text-xs text-muted">Reminder style</label>
 <select
 value={editedFields.nag_style || 'persistent'}
 onChange={(e) => onChange({ ...editedFields, nag_style: e.target.value })}
 className="w-full px-2 py-1 text-sm rounded border border-strong bg-surface"
 >
 <option value="gentle">Gentle</option>
 <option value="steady">Steady</option>
 <option value="persistent">Persistent</option>
 <option value="strict">Strict</option>
 </select>
 </div>
 </div>
 </div>
 )

 default:
 return null
 }
 }

 return (
 <div className="mt-2 p-3 bg-surface rounded-lg border border-tint-brand-edge shadow-sm space-y-3">
 {/* Title */}
 <div>
 <label className="text-xs text-muted">Title</label>
 <input
 type="text"
 value={editedFields.title}
 onChange={(e) => onChange({ ...editedFields, title: e.target.value })}
 className="w-full px-2 py-1 text-sm font-medium rounded border border-strong bg-surface"
 placeholder="Item title..."
 />
 </div>

 {/* Description */}
 <div>
 <label className="text-xs text-muted">Description</label>
 <textarea
 value={editedFields.description || ''}
 onChange={(e) => onChange({ ...editedFields, description: e.target.value })}
 className="w-full px-2 py-1 text-sm rounded border border-strong bg-surface"
 rows={2}
 placeholder="Description (optional)..."
 />
 </div>

 {/* Type-specific fields */}
 {renderTypeSpecificFields()}

 {/* Action buttons */}
 <div className="flex items-center gap-2 pt-2 border-t border-soft">
 <button
 onClick={onSave}
 className="flex-1 px-3 py-1.5 text-sm font-medium rounded bg-brand hover:bg-brand-hover text-on-brand flex items-center justify-center gap-1"
 >
 <Save className="w-3 h-3" />
 Save Changes
 </button>
 <button
 onClick={onCancel}
 className="px-3 py-1.5 text-sm font-medium rounded border border-strong hover:bg-surface-alt flex items-center gap-1"
 >
 <X className="w-3 h-3" />
 Cancel
 </button>
 </div>
 </div>
 )
}

// Rejection dialog component
interface RejectDialogProps {
 item: any
 itemType: string
 onConfirm: (comment?: string) => void
 onCancel: () => void
}

function RejectDialog({ item, onConfirm, onCancel }: RejectDialogProps) {
 const [comment, setComment] = useState('')

 return (
 <div className="mt-2 p-3 bg-tint-bad-soft rounded-lg border border-tint-bad-edge shadow-sm">
 <div className="flex items-start gap-2 mb-2">
 <XCircle className="w-4 h-4 text-bad mt-0.5" />
 <div className="flex-1">
 <p className="text-sm font-medium text-bad">
 Reject"{item.title}"?
 </p>
 <p className="text-xs text-bad mt-1">
 This item won't be saved. A note helps {BRAND.assistant} understand what works better for you.
 </p>
 </div>
 </div>
 
 <div className="mt-2">
 <label className="text-xs text-muted flex items-center gap-1">
 <MessageSquare className="w-3 h-3" /> Reason (optional)
 </label>
 <textarea
 value={comment}
 onChange={(e) => setComment(e.target.value)}
 className="w-full px-2 py-1 text-sm rounded border border-strong bg-surface mt-1"
 rows={2}
 placeholder={`Why doesn't this item work for you? (helps ${BRAND.assistant} improve)`}
 />
 </div>

 <div className="flex items-center gap-2 mt-3">
 <button
 onClick={() => onConfirm(comment || undefined)}
 className="flex-1 px-3 py-1.5 text-sm font-medium rounded bg-bad hover:bg-bad text-on-brand flex items-center justify-center gap-1"
 >
 <XCircle className="w-3 h-3" />
 Reject Item
 </button>
 <button
 onClick={onCancel}
 className="px-3 py-1.5 text-sm font-medium rounded border border-strong hover:bg-surface-alt"
 >
 Cancel
 </button>
 </div>
 </div>
 )
}

// Props for item actions (edit/reject buttons)
interface ItemActionsProps {
 item: any
 itemType: string
 itemState: ItemState
 onEdit: () => void
 onReject: () => void
 onUndoReject: () => void
 /** Optional: bind this proposal as the chat's refine focus ("add detail"). */
 onRefine?: () => void
}

function ItemActions({ itemState, onEdit, onReject, onUndoReject, onRefine }: ItemActionsProps) {
 const { status } = itemState

 if (status === 'rejected') {
 return (
 <div className="flex items-center gap-1">
 <span className="text-xs text-bad flex items-center gap-1">
 <XCircle className="w-3 h-3" />
 Rejected
 </span>
 <button
 onClick={onUndoReject}
 className="text-xs text-brand hover:text-brand-hover underline ml-1"
 >
 Undo
 </button>
 </div>
 )
 }

 if (status === 'edited') {
 return (
 <div className="flex items-center gap-1">
 <span className="text-xs text-brand flex items-center gap-1">
 <Check className="w-3 h-3" />
 Edited
 </span>
 <button
 onClick={onEdit}
 className="p-1 rounded hover:bg-surface-alt"
 title="Edit again"
 >
 <Edit2 className="w-3 h-3 text-muted" />
 </button>
 </div>
 )
 }

 return (
 <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
 {/* Refine-in-chat button — the next message enriches THIS proposal. */}
 {onRefine && (
 <button
 onClick={onRefine}
 className="p-1.5 rounded bg-brand-subtle hover:bg-tint-brand-solid text-brand transition-colors"
 title="Add detail in chat"
 >
 <MessageSquare className="w-3 h-3" />
 </button>
 )}
 {/* Edit button */}
 <button
 onClick={onEdit}
 className="p-1.5 rounded bg-brand-subtle hover:bg-tint-brand-solid text-brand transition-colors"
 title="Edit this item"
 >
 <Edit2 className="w-3 h-3" />
 </button>
 
 {/* Reject button */}
 <button
 onClick={onReject}
 className="p-1.5 rounded bg-surface-alt hover:bg-tint-bad-fill text-muted hover:text-bad transition-colors"
 title="Reject this item"
 >
 <X className="w-3 h-3" />
 </button>
 </div>
 )
}

// Props for the main component
interface DreamHierarchyDisplayProps {
 composedResponse?: ComposedResponse
 className?: string
 // State management callbacks
 itemStates: Map<string, ItemState>
 onItemStateChange: (itemKey: string, state: ItemState) => void
 rejectedItems: RejectedItem[]
 onRejectedItemsChange: (items: RejectedItem[]) => void
 // Commit callback
 onCommitItems?: () => void
 isCommitting?: boolean
 // Check if there are unsaved edits
 hasUnsavedEdits?: boolean
 // Commit status (tombstone)
 isCommitted?: boolean
 committedAt?: string | null
 /** Bind a proposal as the chat's refine focus — the next message adds
  * detail to it instead of creating a duplicate (owner ask 2026-07-05). */
 onRefineItem?: (item: { type: string; title: string }) => void
}

export function DreamHierarchyDisplay({
 composedResponse,
 className = '',
 itemStates,
 onItemStateChange,
 rejectedItems,
 onRejectedItemsChange,
 onCommitItems,
 isCommitting = false,
 isCommitted = false,
 committedAt = null,
 onRefineItem
}: DreamHierarchyDisplayProps) {
 // State for tracking expanded/collapsed items in the tree view
 const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set())
 // State for showing rejection dialog
 const [rejectingItemKey, setRejectingItemKey] = useState<string | null>(null)
 
 const toggleExpanded = (itemKey: string) => {
 setExpandedItems(prev => {
 const newSet = new Set(prev)
 if (newSet.has(itemKey)) {
 newSet.delete(itemKey)
 } else {
 newSet.add(itemKey)
 }
 return newSet
 })
 }

 // Get item state or create default
 const getItemState = useCallback((item: any, itemType: string): ItemState => {
 const key = `${itemType}:${item.title}`
 return itemStates.get(key) || {
 status: 'pending',
 originalItem: item
 }
 }, [itemStates])

 // Handle edit start
 const handleEditStart = useCallback((item: any, itemType: string) => {
 const key = `${itemType}:${item.title}`
 const currentState = getItemState(item, itemType)

 // Get fields from item (may be nested in fields object)
 const fields = item.fields || {}
 
 // Handle recurrence - it could be a simple string or a rich object
 let simpleRecurrence: string | undefined
 let richRecurrence: RecurrenceRule | undefined
 
 const rawRecurrence = item.recurrence || fields.recurrence
 if (rawRecurrence) {
 if (typeof rawRecurrence === 'string') {
 simpleRecurrence = rawRecurrence
 } else if (typeof rawRecurrence === 'object') {
 richRecurrence = rawRecurrence as RecurrenceRule
 // Also set simple recurrence for backwards compatibility
 simpleRecurrence = rawRecurrence.type || 'weekly'
 }
 }

 // Initialize edit fields from original item.
 // Every ItemEdit key must be seeded here: the save path writes the whole
 // ItemEdit bag back into `fields`, so an unseeded key would clobber the
 // planner's original value with undefined.
 const editedFields: ItemEdit = currentState.editedFields || {
 title: item.title,
 description: item.description || '',
 life_area: item.life_area,
 priority: item.priority || fields.priority,
 target_date: item.target_date || fields.target_date,
 start_date: item.start_date || fields.start_date,
 duration_minutes: item.duration_minutes || fields.duration_minutes,
 recurrence: simpleRecurrence as ItemEdit['recurrence'],
 energy_level: item.energy_level || fields.energy_level,
 scheduled_time: item.scheduled_time || fields.scheduled_time,
 severity: item.severity || fields.severity,
 // Rich scheduling intent from planner
 recurrence_rule: richRecurrence,
 preferred_days: fields.preferred_days,
 preferred_time_window: fields.preferred_time_window,
 preferred_start_time: item.preferred_start_time || fields.preferred_start_time,
 schedule_candidate: fields.schedule_candidate,
 // Habit-specific
 habit_status: item.habit_status || fields.habit_status,
 target_cadence: item.target_cadence || fields.target_cadence,
 rrule: item.rrule || fields.rrule,
 follow_through_mode: item.follow_through_mode || fields.follow_through_mode,
 nag_style: item.nag_style || fields.nag_style,
 // Skill-specific
 skill_status: item.skill_status || fields.skill_status,
 current_level: item.current_level || fields.current_level,
 target_level: item.target_level || fields.target_level
 }

 onItemStateChange(key, {
 ...currentState,
 status: 'editing',
 editedFields
 })
 }, [getItemState, onItemStateChange])

 // Handle edit save
 const handleEditSave = useCallback((item: any, itemType: string) => {
 const key = `${itemType}:${item.title}`
 const currentState = getItemState(item, itemType)
 
 onItemStateChange(key, {
 ...currentState,
 status: 'edited'
 })
 }, [getItemState, onItemStateChange])

 // Handle edit cancel
 const handleEditCancel = useCallback((item: any, itemType: string) => {
 const key = `${itemType}:${item.title}`
 const currentState = getItemState(item, itemType)
 
 // If item was previously edited, keep it as edited
 // Otherwise, reset to pending
 const newStatus = currentState.status === 'edited' ? 'edited' : 'pending'
 
 onItemStateChange(key, {
 ...currentState,
 status: newStatus
 })
 }, [getItemState, onItemStateChange])

 // Handle edit field change
 const handleEditFieldChange = useCallback((item: any, itemType: string, fields: ItemEdit) => {
 const key = `${itemType}:${item.title}`
 const currentState = getItemState(item, itemType)
 
 onItemStateChange(key, {
 ...currentState,
 editedFields: fields
 })
 }, [getItemState, onItemStateChange])

 // Handle reject start
 const handleRejectStart = useCallback((item: any, itemType: string) => {
 const key = `${itemType}:${item.title}`
 setRejectingItemKey(key)
 }, [])

 const items_summary = composedResponse?.items_summary

 const hierarchyOnlyItems = useMemo(() => {
 if (!items_summary) return []
 return items_summary.filter((item: any) => item.type !== 'identity' && item.type !== 'person')
 }, [items_summary])

 // Collect all descendant items by walking the full flat item list via parent refs.
 // This works even when the tree structure isn't fully built (missing parent links
 // cause items to land at root level, but they still carry parent.type:parent.title).
 const collectAllDescendants = useCallback((rejectedType: string, rejectedTitle: string): Array<{ item: any; type: string }> => {
 const allItems = hierarchyOnlyItems
 if (!allItems.length) return []

 // Seed: the rejected item's key
 const rejectedKeys = new Set<string>()
 rejectedKeys.add(`${rejectedType}:${rejectedTitle}`)

 // Multi-pass: keep finding children whose parent key is in rejectedKeys
 // until no new items are found (handles arbitrary depth).
 const descendants: Array<{ item: any; type: string }> = []
 let changed = true
 while (changed) {
 changed = false
 for (const item of allItems) {
 const itemKey = `${item.type}:${item.title}`
 if (rejectedKeys.has(itemKey)) continue // already collected or is the root
 if (item.parent) {
 const parentKey = `${item.parent.type}:${item.parent.title}`
 if (rejectedKeys.has(parentKey)) {
 rejectedKeys.add(itemKey)
 descendants.push({ item, type: item.type })
 changed = true
 }
 }
 }
 }

 // Also collect tree-attached children (from flatTreeRoots) that might not
 // have parent refs but were attached via the tree builder
 const walkTree = (node: any) => {
 const childrenTypes = ['aspirations', 'goals', 'tasks', 'action_items', 'roadblocks', 'knowledge_skills', 'skills', 'habits']
 childrenTypes.forEach(childType => {
 const children = node[childType] || []
 const singularType = childType.replace(/s$/, '')
 if (Array.isArray(children)) {
 children.forEach((child: any) => {
 const ck = `${singularType}:${child.title}`
 if (!rejectedKeys.has(ck)) {
 rejectedKeys.add(ck)
 descendants.push({ item: child, type: singularType })
 }
 walkTree(child)
 })
 }
 })
 // action_items in fields
 const fieldAIs = node.fields?.action_items || []
 if (Array.isArray(fieldAIs)) {
 fieldAIs.forEach((ai: any) => {
 const ak = `action_item:${ai.title || ai.description}`
 if (!rejectedKeys.has(ak)) {
 rejectedKeys.add(ak)
 descendants.push({ item: ai, type: 'action_item' })
 }
 })
 }
 }

 return descendants
 }, [hierarchyOnlyItems])

 // Handle reject confirm — cascading: reject all children underneath
 const handleRejectConfirm = useCallback((item: any, itemType: string, comment?: string) => {
 const key = `${itemType}:${item.title}`
 const now = new Date().toISOString()
 const cascadeComment = comment ? `(cascaded) ${comment}` : '(cascaded from parent rejection)'

 // Reject the item itself
 onItemStateChange(key, {
 status: 'rejected',
 originalItem: item,
 rejectionComment: comment
 })

 const newRejected: RejectedItem[] = [{
 type: itemType,
 title: item.title,
 originalDescription: item.description,
 rejectionComment: comment,
 rejectedAt: now
 }]

 // Cascade: reject all descendants (walks flat list + tree)
 const descendants = collectAllDescendants(itemType, item.title)

 // Also walk the tree node itself for attached children
 const walkTree = (node: any) => {
 const childrenTypes = ['aspirations', 'goals', 'tasks', 'action_items', 'roadblocks', 'knowledge_skills', 'skills', 'habits']
 childrenTypes.forEach(childType => {
 const children = node[childType] || []
 const singularType = childType.replace(/s$/, '')
 if (Array.isArray(children)) {
 children.forEach((child: any) => {
 const ck = `${singularType}:${child.title}`
 const alreadyFound = descendants.some(d => `${d.type}:${d.item.title}` === ck)
 if (!alreadyFound) {
 descendants.push({ item: child, type: singularType })
 }
 walkTree(child)
 })
 }
 })
 }
 walkTree(item)

 for (const desc of descendants) {
 const childKey = `${desc.type}:${desc.item.title}`
 onItemStateChange(childKey, {
 status: 'rejected',
 originalItem: desc.item,
 rejectionComment: cascadeComment
 })
 newRejected.push({
 type: desc.type,
 title: desc.item.title,
 originalDescription: desc.item.description,
 rejectionComment: cascadeComment,
 rejectedAt: now
 })
 }

 onRejectedItemsChange([...rejectedItems, ...newRejected])

 // Close dialog
 setRejectingItemKey(null)
 }, [onItemStateChange, rejectedItems, onRejectedItemsChange, collectAllDescendants])

 // Handle undo reject
 const handleUndoReject = useCallback((item: any, itemType: string) => {
 const key = `${itemType}:${item.title}`
 
 // Update item state to pending
 onItemStateChange(key, {
 status: 'pending',
 originalItem: item
 })

 // Remove from rejected items list
 onRejectedItemsChange(rejectedItems.filter(ri => 
 !(ri.type === itemType && ri.title === item.title)
 ))
 }, [onItemStateChange, rejectedItems, onRejectedItemsChange])

 // Check if any items are currently being edited
 const isAnyItemEditing = Array.from(itemStates.values()).some(s => s.status === 'editing')

 // Count total items and status - must count ALL items in the hierarchy, not just those in itemStates
 const getItemCounts = useCallback(() => {
 let total = 0
 let edited = 0
 let rejected = 0
 let pending = 0

 // Helper to count items recursively
 const countItem = (item: any, itemType: string) => {
 const key = `${itemType}:${item.title}`
 const state = itemStates.get(key)
 
 total++
 if (state) {
 if (state.status === 'edited') edited++
 else if (state.status === 'rejected') rejected++
 else if (state.status === 'pending') pending++
 } else {
 // Item hasn't been interacted with yet - it's pending by default
 pending++
 }

 // Count children recursively
 const childrenTypes = ['aspirations', 'goals', 'tasks', 'action_items', 'roadblocks', 'knowledge_skills', 'skills', 'habits']
 let hasActionItems = false
 childrenTypes.forEach(childType => {
 const children = item[childType] || item.children?.[childType.replace(/s$/, '')] || []
 const singularType = childType.replace(/s$/, '')
 if (Array.isArray(children) && children.length > 0) {
 if (singularType === 'action_item') hasActionItems = true
 children.forEach((child: any) => countItem(child, singularType))
 }
 })

 // Also check for action_items in fields
 // BUT only if we haven't already counted from item.action_items (prevents duplicates)
 if (!hasActionItems) {
 const fieldActionItems = item.fields?.action_items || []
 if (Array.isArray(fieldActionItems) && fieldActionItems.length > 0) {
 fieldActionItems.forEach((ai: any) => countItem(ai, 'action_item'))
 }
 }
 }

 // Count from ONE source only (matching rendering logic)
 // Priority: items_hierarchy (nested) > items_summary (flat)
 if (composedResponse?.items_hierarchy && composedResponse.items_hierarchy.length > 0) {
 // Count from items_hierarchy (nested items)
 composedResponse.items_hierarchy.forEach((item: any) => {
 countItem(item, item.type || 'dream')
 })
 } else if (composedResponse?.items_summary) {
 composedResponse.items_summary
 .filter((item: any) => item.type !== 'identity' && item.type !== 'person')
 .forEach((item: any) => {
 countItem(item, item.type || 'goal')
 })
 }

 // Always include identity/person items in counts (they have edit/skip controls)
 if (composedResponse?.items_summary) {
 composedResponse.items_summary
 .filter((item: any) => item.type === 'identity' || item.type === 'person')
 .forEach((item: any) => {
 countItem(item, item.type)
 })
 }

 return { total, edited, rejected, pending }
 }, [itemStates, composedResponse])

 // ── Destructure composedResponse (may be null) ──
 const items_hierarchy = composedResponse?.items_hierarchy
 const thought_process = composedResponse?.thought_process
 const recommendations = composedResponse?.recommendations
 const clarifying_questions = composedResponse?.clarifying_questions

 const hasHierarchy = !!(items_hierarchy && items_hierarchy.length > 0)
 const hasFlatItems = !!(items_summary && items_summary.some((item: any) => item.type !== 'identity' && item.type !== 'person'))
 const hasThoughts = !!(thought_process && thought_process.length > 0)
 const hasRecommendations = !!(recommendations && recommendations.length > 0)
 const hasQuestions = !!(clarifying_questions && clarifying_questions.length > 0)
 const hasItems = hasHierarchy || hasFlatItems
 const hasInsights = hasThoughts || hasRecommendations || hasQuestions

 // ── All hooks MUST be above early returns ──
 const identityPersonItems = useMemo(() => {
 if (!items_summary) return []
 return items_summary.filter((item: any) => item.type === 'identity' || item.type === 'person')
 }, [items_summary])

 const hasPeople = identityPersonItems.length > 0

 type TabId = 'items' | 'insights'
 const tabs = useMemo(() => {
 const t: Array<{ id: TabId; label: string; icon: React.ReactNode; count?: number }> = []
 const counts = getItemCounts()
 if (hasItems || hasPeople) t.push({ id: 'items', label: 'Items', icon: <Star className="w-4 h-4" />, count: counts.total })
 if (hasInsights) t.push({ id: 'insights', label: 'Insights', icon: <Lightbulb className="w-4 h-4" /> })
 return t
 }, [hasItems, hasPeople, hasInsights, getItemCounts, identityPersonItems.length])

 const [activeTab, setActiveTab] = useState<TabId>('items')

 const flatTreeRoots = useMemo(() => {
 if (!hierarchyOnlyItems.length) return []
 const itemsMap = new Map<string, any>()
 const rootItems: any[] = []
 hierarchyOnlyItems.forEach((item: any) => {
 const key = `${item.type}:${item.title}`
 itemsMap.set(key, { ...item, _children: [] })
 })
 hierarchyOnlyItems.forEach((item: any) => {
 const key = `${item.type}:${item.title}`
 const itemWithChildren = itemsMap.get(key)!
 if (item.parent) {
 const parentKey = `${item.parent.type}:${item.parent.title}`
 const parentItem = itemsMap.get(parentKey)
 if (parentItem) {
 if (!parentItem[`${item.type}s`]) parentItem[`${item.type}s`] = []
 parentItem[`${item.type}s`].push(itemWithChildren)
 } else {
 rootItems.push(itemWithChildren)
 }
 } else {
 rootItems.push(itemWithChildren)
 }
 })
 return rootItems
 }, [hierarchyOnlyItems])

 // ── Early returns (after all hooks) ──

 const hasAnything = hasItems || hasPeople || hasInsights

 if (!composedResponse || !hasAnything) {
 return (
 <div className={`h-full ${className}`}>
 <HortonElephantGame />
 </div>
 )
 }

 // Render a single item with edit/reject capabilities
 const renderItem = (item: any, itemType: string, depth: number = 0) => {
 const key = `${itemType}:${item.title}`
 const itemState = getItemState(item, itemType)
 const isEditing = itemState.status === 'editing'
 const isRejected = itemState.status === 'rejected'
 const isRejecting = rejectingItemKey === key
 const indent = depth * 24

 // Get children if any
 const childrenTypes = ['aspirations', 'goals', 'tasks', 'action_items', 'roadblocks', 'knowledge_skills', 'skills', 'habits']
 const allChildren: Array<{ item: any; type: string }> = []
 let hasActionItems = false
 
 childrenTypes.forEach(childType => {
 const children = item[childType] || item.children?.[childType.replace(/s$/, '')] || []
 const singularType = childType.replace(/s$/, '')
 if (Array.isArray(children) && children.length > 0) {
 if (singularType === 'action_item') hasActionItems = true
 children.forEach((child: any) => allChildren.push({ item: child, type: singularType }))
 }
 })

 // Also check for action_items in fields
 // BUT only if we haven't already added from item.action_items (prevents duplicates)
 if (!hasActionItems) {
 const fieldActionItems = item.fields?.action_items || []
 if (Array.isArray(fieldActionItems) && fieldActionItems.length > 0) {
 fieldActionItems.forEach((ai: any) => allChildren.push({ item: ai, type: 'action_item' }))
 }
 }

 const hasChildren = allChildren.length > 0
 const isExpanded = expandedItems.has(key) || depth === 0

 return (
 <div key={key} style={{ marginLeft: `${indent}px` }} className="mb-2">
 {/* Item Card */}
 <div className={`p-3 rounded-lg border-l-4 ${typeColors[itemType] || 'border-l-gray-400 bg-surface-alt'} 
 ${isRejected ? 'opacity-50' : ''} ${isEditing ? 'ring-2 ring-brand' : ''}`}>
 <div className="flex items-start gap-2">
 {/* Expand/collapse toggle */}
 {hasChildren && (
 <button onClick={() => toggleExpanded(key)} className="mt-0.5 flex-shrink-0">
 {isExpanded ? <ChevronDown className="w-4 h-4 text-muted" /> : <ChevronRight className="w-4 h-4 text-muted" />}
 </button>
 )}
 
 {/* Type icon */}
 {typeIcons[itemType] || <Star className="w-4 h-4 text-muted" />}
 
 <div className="flex-1 min-w-0">
 <div className="flex items-start justify-between gap-2">
 <div className="flex-1 min-w-0">
 {/* Type label */}
 <div className="flex items-center gap-2 mb-1 flex-wrap">
 <span className={`px-2 py-0.5 rounded text-xs font-semibold uppercase tracking-wide ${
 itemType === 'dream' ? 'bg-tint-warn-solid text-warn ' :
 itemType === 'aspiration' ? 'bg-tint-info-solid text-info ' :
 itemType === 'goal' ? 'bg-tint-brand-solid text-brand ' :
 itemType === 'task' ? 'bg-tint-ok-solid text-ok ' :
 itemType === 'action_item' ? 'bg-tint-ok-solid text-ok' :
 itemType === 'roadblock' ? 'bg-tint-warn-solid text-warn ' :
 itemType === 'skill' ? 'bg-tint-info-solid text-info' :
 itemType === 'habit' ? 'bg-tint-ok-solid text-ok' :
 'bg-surface-alt text-primary '
 }`}>
 {itemType.replace('_', ' ')}
 </span>
 {item.life_area && (
 <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${lifeAreaColors[item.life_area] || lifeAreaColors.other}`}>
 {item.life_area.replace('_', ' ')}
 </span>
 )}
 {item.is_existing_link && (
 <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-tint-warn-fill text-warn flex items-center gap-1">
 <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
 Linked to existing
 </span>
 )}
 {itemState.status === 'edited' && (
 <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-brand-subtle text-brand">
 ✏️ Edited
 </span>
 )}
 </div>
 
 {/* Title */}
 <h5 className={`font-medium text-sm text-primary break-words ${isRejected ? 'line-through' : ''}`}>
 {itemState.editedFields?.title || item.title}
 </h5>
 
 {/* Dedup info — shows which existing item this links to */}
 {item.is_existing_link && item.dedup_info?.existing_title && (
 <p className="text-xs text-warn mt-1 flex items-center gap-1">
 <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
 Updates existing:"{item.dedup_info.existing_title}"
 </p>
 )}

 {/* Description */}
 {(itemState.editedFields?.description || item.description) && (
 <p className="text-xs text-secondary mt-1 break-words">
 {itemState.editedFields?.description || item.description}
 </p>
 )}

 {/* What's getting in the way — demoted-roadblock blocker annotations
 carried on this item (roadblock is no longer a separate node). */}
 {(() => {
 const blockers = (item.fields?.blockers || item.blockers) as any[] | undefined
 if (!Array.isArray(blockers) || blockers.length === 0) return null
 return (
 <div className="mt-2 rounded-md border border-tint-warn-edge bg-tint-warn-veil px-2 py-1.5">
 <div className="flex items-center gap-1 text-xs font-medium text-warn">
 <AlertTriangle className="w-3 h-3 flex-shrink-0" />
 What's getting in the way
 </div>
 <ul className="mt-1 space-y-0.5">
 {blockers.map((b: any, i: number) => (
 <li key={b?.id || i} className="text-xs text-secondary break-words flex items-start gap-1">
 <span className="text-warn mt-[1px]">•</span>
 <span>{b?.reason || b?.title || 'Something is getting in the way'}</span>
 </li>
 ))}
 </ul>
 </div>
 )
 })()}

 {/* Scheduling Intent (from planner) - for tasks and habits */}
 {(itemType === 'task' || itemType === 'habit') && item.fields && (
 (() => {
 const fields = item.fields
 const hasSchedulingIntent =
 fields.recurrence ||
 fields.preferred_days ||
 fields.preferred_time_window ||
 fields.preferred_start_time ||
 fields.duration_minutes
 
 if (!hasSchedulingIntent) return null
 
 // Format recurrence nicely
 const formatRecurrence = (rec: any) => {
 if (!rec) return null
 const { type, interval, days_of_week, end_condition } = rec
 let pattern = ''
 
 if (type === 'daily') {
 pattern = interval === 1 ? 'Daily' : `Every ${interval} days`
 } else if (type === 'weekly') {
 const dayMap: Record<string, string> = { 'mon': 'Mon', 'tue': 'Tue', 'wed': 'Wed', 'thu': 'Thu', 'fri': 'Fri', 'sat': 'Sat', 'sun': 'Sun' }
 const daysStr = days_of_week?.map((d: string) => dayMap[d.toLowerCase()] || d).join(', ') || ''
 pattern = interval === 1 
 ? `Weekly${daysStr ? ` on ${daysStr}` : ''}`
 : `Every ${interval} weeks${daysStr ? ` on ${daysStr}` : ''}`
 } else if (type === 'monthly') {
 pattern = interval === 1 ? 'Monthly' : `Every ${interval} months`
 } else {
 pattern = type || 'Recurring'
 }
 
 if (end_condition === 'until_done') {
 pattern += ' (ongoing)'
 }
 
 return pattern
 }
 
 const formatTimeWindow = (window: string) => {
 const map: Record<string, string> = {
 'early_morning': '🌅 Early Morning',
 'morning': '☀️ Morning',
 'afternoon': '🌤️ Afternoon',
 'evening': '🌆 Evening',
 'night': '🌙 Night',
 'any': 'Any time'
 }
 return map[window] || window
 }
 
 return (
 <div className="mt-2 p-2 bg-brand-subtle rounded border border-brand-subtle">
 <div className="flex items-center gap-1 text-xs font-medium text-brand mb-1">
 <Repeat className="w-3 h-3" />
 Scheduling Intent
 </div>
 <div className="space-y-0.5 text-xs text-brand">
 {fields.recurrence && (
 <div className="flex items-center gap-1">
 <span className="font-medium">🔄 Recurrence:</span>
 <span>{formatRecurrence(fields.recurrence)}</span>
 </div>
 )}
 {fields.preferred_days && fields.preferred_days.length > 0 && !fields.recurrence && (
 <div className="flex items-center gap-1">
 <span className="font-medium">📅 Preferred:</span>
 <span>{fields.preferred_days.map((d: string) => d.charAt(0).toUpperCase() + d.slice(1)).join(', ')}</span>
 </div>
 )}
 {fields.preferred_time_window && (
 <div className="flex items-center gap-1">
 <span className="font-medium">⏰ Time:</span>
 <span>{formatTimeWindow(fields.preferred_time_window)}</span>
 </div>
 )}
 {fields.preferred_start_time && (
 <div className="flex items-center gap-1">
 <Clock className="w-3 h-3" />
 <span className="font-medium">Suggested time:</span>
 <span>{fields.preferred_start_time}</span>
 </div>
 )}
 {fields.duration_minutes && (
 <div className="flex items-center gap-1">
 <Clock className="w-3 h-3" />
 <span>{fields.duration_minutes} min</span>
 </div>
 )}
 {fields.schedule_candidate && (
 <div className="text-ok font-medium">
 ✓ Ready for scheduling
 </div>
 )}
 </div>
 </div>
 )
 })()
 )}

 {/* Show rejection comment if rejected */}
 {isRejected && itemState.rejectionComment && (
 <p className="text-xs text-bad mt-1 italic flex items-center gap-1">
 <MessageSquare className="w-3 h-3" />
 Reason: {itemState.rejectionComment}
 </p>
 )}
 </div>
 
 {/* Action buttons */}
 <ItemActions
 item={item}
 itemType={itemType}
 itemState={itemState}
 onEdit={() => handleEditStart(item, itemType)}
 onReject={() => handleRejectStart(item, itemType)}
 onUndoReject={() => handleUndoReject(item, itemType)}
 onRefine={onRefineItem ? () => onRefineItem({ type: itemType, title: item.title }) : undefined}
 />
 </div>
 </div>
 </div>
 </div>

 {/* Edit form (inline) */}
 {isEditing && itemState.editedFields && (
 <ItemEditForm
 item={item}
 itemType={itemType}
 editedFields={itemState.editedFields}
 onChange={(fields) => handleEditFieldChange(item, itemType, fields)}
 onSave={() => handleEditSave(item, itemType)}
 onCancel={() => handleEditCancel(item, itemType)}
 />
 )}

 {/* Rejection dialog */}
 {isRejecting && (
 <RejectDialog
 item={item}
 itemType={itemType}
 onConfirm={(comment) => handleRejectConfirm(item, itemType, comment)}
 onCancel={() => setRejectingItemKey(null)}
 />
 )}

 {/* Render children recursively */}
 {hasChildren && isExpanded && (
 <div className="mt-2">
 {allChildren.map(({ item: child, type }) => 
 renderItem(child, type, depth + 1)
 )}
 </div>
 )}
 </div>
 )
 }

 if (!hasItems && !hasPeople && !hasThoughts && !hasRecommendations && !hasQuestions) {
 return (
 <div className={`text-center text-muted py-8 ${className}`}>
 <Star className="w-12 h-12 mx-auto mb-3 opacity-30" />
 <p>Processing your input...</p>
 </div>
 )
 }

 const counts = getItemCounts()

 return (
 <div className={`space-y-4 ${className}`}>
 {/* Unsaved edits warning */}
 {isAnyItemEditing && (
 <div className="bg-tint-warn-soft rounded-lg p-3 border border-tint-warn-edge">
 <div className="flex items-center gap-2 text-warn text-sm">
 <AlertCircle className="w-4 h-4 flex-shrink-0" />
 <p className="font-medium">You have unsaved edits — save or cancel before continuing.</p>
 </div>
 </div>
 )}

 {/* Tab bar */}
 {tabs.length > 1 && (
 <div className="flex gap-1 p-1 bg-surface-alt rounded-lg overflow-x-auto">
 {tabs.map(tab => (
 <button
 key={tab.id}
 onClick={() => setActiveTab(tab.id)}
 className={`flex-1 min-w-0 px-3 py-2 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-1.5
 ${activeTab === tab.id
 ? 'bg-surface text-primary shadow-sm'
 : 'text-muted hover:text-secondary dark:hover:text-muted/50'
 }`}
 >
 {tab.icon}
 <span className="truncate">{tab.label}</span>
 {tab.count !== undefined && (
 <span className={`text-xs px-1.5 py-0.5 rounded-full ${
 activeTab === tab.id
 ? 'bg-surface-alt text-secondary'
 : 'bg-surface-alt/60 text-muted'
 }`}>{tab.count}</span>
 )}
 </button>
 ))}
 </div>
 )}

 {/* ── Items tab ── */}
 {(activeTab === 'items' || tabs.length <= 1) && (hasItems || hasPeople) && (
 <div>
 <div className="flex items-center justify-between mb-3">
 <p className="text-xs text-muted">
 Edit or skip items, then save when ready.
 </p>
 <div className="text-xs text-muted flex items-center gap-3">
 {counts.edited > 0 && (
 <span className="flex items-center gap-1 text-brand">
 <Edit2 className="w-3 h-3" /> {counts.edited} edited
 </span>
 )}
 {counts.rejected > 0 && (
 <span className="flex items-center gap-1 text-bad">
 <XCircle className="w-3 h-3" /> {counts.rejected} skipped
 </span>
 )}
 </div>
 </div>

 {/* Dream Hierarchy (Nested Structure) */}
 {hasHierarchy && (
 <div className="space-y-3">
 {items_hierarchy!
   .filter((item: any) => item.type !== 'identity' && item.type !== 'person')
   .map((item) => (
   renderItem(item, item.type || 'dream', 0)
 ))}
 </div>
 )}

 {/* Flat Items Summary (V4 Format) - Build Tree Structure */}
 {hasFlatItems && !hasHierarchy && (
 <div className="space-y-3">
 {flatTreeRoots.map(item => renderItem(item, item.type, 0))}
 </div>
 )}

 {/* People & Identity — shown inline within Items tab */}
 {hasPeople && (
 <div className="mt-4 space-y-3">
 <h4 className="text-xs font-semibold text-muted uppercase tracking-wide">People</h4>
 {identityPersonItems.map((item: any, idx: number) => {
 const personType = item.type || 'person'
 const personState = getItemState(item, personType)
 const personKey = `${personType}:${item.title}`
 const isPersonEditing = personState.status === 'editing'
 const isPersonRejected = personState.status === 'rejected'
 const isPersonRejecting = rejectingItemKey === personKey

 return (
 <div key={idx}>
 <div className={`p-4 rounded-lg border-l-4 ${
 isPersonRejected
 ? 'border-l-tint-bad-edge bg-tint-bad-wash /10 opacity-60'
 : item.type === 'identity'
 ? 'border-l-tint-info-rule bg-tint-info-wash '
 : 'border-l-tint-info-rule bg-tint-info-wash /20'
 }`}>
 <div className="flex items-center justify-between">
 <div className="flex items-center gap-2 mb-1">
 {item.type === 'identity'
 ? <User className="w-5 h-5 text-info" />
 : <Users className="w-5 h-5 text-info" />}
 <span className="font-medium text-primary">{item.title}</span>
 <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
 item.type === 'identity'
 ? 'bg-tint-info-fill text-info '
 : 'bg-tint-info-fill text-info '
 }`}>{item.type === 'identity' ? 'You' : 'Person'}</span>
 </div>
 <ItemActions
 item={item}
 itemType={personType}
 itemState={personState}
 onEdit={() => handleEditStart(item, personType)}
 onReject={() => handleRejectStart(item, personType)}
 onUndoReject={() => handleUndoReject(item, personType)}
 />
 </div>
 {item.description && !isPersonEditing && (
 <p className="text-sm text-secondary ml-7">{item.description}</p>
 )}
 {item.life_area && !isPersonEditing && (
 <span className="inline-block mt-1 ml-7 text-xs px-2 py-0.5 rounded-full bg-surface-alt text-muted">
 {item.life_area}
 </span>
 )}
 </div>

 {isPersonEditing && personState.editedFields && (
 <ItemEditForm
 item={item}
 itemType={personType}
 editedFields={personState.editedFields}
 onChange={(fields) => handleEditFieldChange(item, personType, fields)}
 onSave={() => handleEditSave(item, personType)}
 onCancel={() => handleEditCancel(item, personType)}
 />
 )}

 {isPersonRejecting && (
 <RejectDialog
 item={item}
 itemType={personType}
 onConfirm={(comment) => handleRejectConfirm(item, personType, comment)}
 onCancel={() => setRejectingItemKey(null)}
 />
 )}
 </div>
 )
 })}
 </div>
 )}
 </div>
 )}

 {/* ── Insights tab ── */}
 {activeTab === 'insights' && hasInsights && (
 <div className="space-y-4">
 {hasThoughts && (
 <div className="bg-brand-subtle rounded-lg p-4 border border-brand-subtle">
 <h3 className="font-semibold text-brand mb-2 flex items-center gap-2 text-sm">
 <Lightbulb className="w-4 h-4" />
 How I understood your situation:
 </h3>
 <ul className="space-y-1">
 {thought_process!.map((thought, idx) => (
 <li key={idx} className="text-sm text-brand flex items-start gap-2">
 <span className="text-brand">•</span>
 {thought}
 </li>
 ))}
 </ul>
 </div>
 )}

 {hasRecommendations && (
 <div className="bg-tint-ok-soft rounded-lg p-4 border border-tint-ok-edge">
 <h3 className="font-semibold text-ok mb-2 flex items-center gap-2 text-sm">
 <Target className="w-4 h-4" />
 Recommendations:
 </h3>
 <ol className="space-y-1 list-decimal list-inside">
 {recommendations!.map((rec, idx) => (
 <li key={idx} className="text-sm text-ok">{rec}</li>
 ))}
 </ol>
 </div>
 )}

 {hasQuestions && (
 <div className="bg-tint-warn-soft rounded-lg p-4 border border-tint-warn-edge">
 <h3 className="font-semibold text-warn mb-2 flex items-center gap-2 text-sm">
 <Lightbulb className="w-4 h-4" />
 Questions to consider:
 </h3>
 <ul className="space-y-1">
 {clarifying_questions!.map((q, idx) => (
 <li key={idx} className="text-sm text-warn flex items-start gap-2">
 <span className="text-warn">?</span>
 {q}
 </li>
 ))}
 </ul>
 </div>
 )}
 </div>
 )}

 {/* Save Button — always visible when items exist */}
 {(hasItems || hasPeople) && onCommitItems && (
 <div className="pt-3 border-t border-soft">
 {isCommitted ? (
 <div className="w-full py-3 px-4 rounded-lg bg-tint-ok-soft border-2 border-tint-ok-edge">
 <div className="flex items-center justify-center gap-2 text-ok">
 <Check className="w-5 h-5 text-ok" />
 <span className="font-medium">Added to Your Life Map</span>
 </div>
 {committedAt && (
 <p className="text-xs text-ok text-center mt-2">
 Added on {new Date(committedAt).toLocaleString()}
 </p>
 )}
 <p className="text-xs text-ok text-center mt-1">
 Head to"Schedule" when you're ready to start working on these
 </p>
 </div>
 ) : (
 <>
 <button
 onClick={onCommitItems}
 disabled={isCommitting || isAnyItemEditing}
 className={`w-full py-3 px-4 rounded-lg font-medium text-on-brand flex items-center justify-center gap-2 transition-all
 ${isCommitting || isAnyItemEditing
 ? 'bg-muted/50 cursor-not-allowed'
 : 'bg-gradient-to-r from-brand to-info hover:from-brand hover:to-info shadow-md hover:shadow-lg'
 }`}
 >
 {isCommitting ? (
 <>
 <Loader2 className="w-5 h-5 animate-spin" />
 Saving to your Life Map...
 </>
 ) : isAnyItemEditing ? (
 <>
 <AlertCircle className="w-5 h-5" />
 Save your edits first
 </>
 ) : (
 <>
 <CheckCircle2 className="w-5 h-5" />
 Save to My Life Map
 </>
 )}
 </button>
 {!isAnyItemEditing && (
 <p className="text-xs text-muted text-center mt-2">
 You can schedule these whenever you're ready
 </p>
 )}
 </>
 )}
 </div>
 )}

 {/* Response metadata intentionally hidden — internal pipeline labels */}
 </div>
 )
}

export default DreamHierarchyDisplay
